import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { connect } from 'react-redux';
import { Col, Row, CardDeck, Container, Button, Form, Spinner } from "react-bootstrap";
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import styled from 'styled-components';

import constants from "../../const";
import PositionCard from "./position-card.js";
import TimelineComponent from "../common/TimelineComponent.js";
import Modal from '../common/Modal';
import PositionSelector from '../common/PositionSelector';
import { FloatingWindow } from '../file-browser/FloatingVideoPlayer';

// ============================================================================
// Styled Components
// ============================================================================

const ModernButtonStyle = styled.button`
  border-radius: 6px;
  font-weight: 600;
  padding: 6px 12px;
  transition: all 0.3s ease;
  border: none;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-family: inherit;

  &.btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &.btn-light {
    background: #ffffff;
    color: #667eea;
    border: 2px solid #667eea;

    &:hover:not(:disabled) {
      background: #667eea;
      color: white;
      transform: translateY(-2px);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;

const ModernButton = (props) => {
  const { variant, onClick, disabled, children, style } = props;
  const className = variant ? `btn-${variant}` : '';
  
  return (
    <ModernButtonStyle
      className={className}
      onClick={onClick}
      disabled={disabled}
      style={style}
      type="button"
    >
      {children}
    </ModernButtonStyle>
  );
};

const PositionGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
  pointer-events: auto;
`;

const SearchInputStyle = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;

const CategorySection = styled.div`
  margin-bottom: 20px;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  user-select: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  .expand-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    transition: transform 0.3s ease;
    transform: ${props => props.expanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
  }
`;

const PositionGridContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 12px;
  pointer-events: auto;
`;

const PositionSelectCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #e0e0e0;
  background: white;
  pointer-events: auto;

  &:hover {
    border-color: #667eea;
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(102, 126, 234, 0.2);
  }

  &.selected {
    border-color: #667eea;
    background: linear-gradient(135deg, #f0f4ff 0%, #e3e9ff 100%);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  .position-image {
    width: 100%;
    aspect-ratio: 125 / 83;
    object-fit: contain;
    margin-bottom: 6px;
    border-radius: 4px;
    background-color: #f5f5f5;
    pointer-events: none;
  }

  .position-info {
    text-align: center;
    width: 100%;
    pointer-events: none;
    
    .position-name {
      font-size: 12px;
      font-weight: 600;
      color: #333;
      margin-bottom: 2px;
      word-break: break-word;
    }
  }
`;

function PositionAdder(props) {
  var token = "";
  if (localStorage.getItem("token") != null) {
    token = localStorage.getItem("token");
  }

  const [allPositions, setAllPositions] = useState([]);
  const [allPositionTags, setAllPositionTags] = useState([]);
  const [scenePositions, setScenePositions] = useState([]);
  const [positionBar, setPositionBar] = useState([]);

  // Timeline state
  const [actions, setActions] = useState([]);
  const [timelineCurrentTime, setTimelineCurrentTime] = useState(0);
  const [timelinePlaying, setTimelinePlaying] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoTime, setVideoTime] = useState(0);
  const [timelineCollapsed, setTimelineCollapsed] = useState(true);
  const syncFromTimelineRef = useRef(false);
  const vidRef = useRef(null);
  const lastKeyTimeRef = useRef(0);

  // New position form state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPositionGrid, setShowPositionGrid] = useState(false);
  const [selectedPositionForAdd, setSelectedPositionForAdd] = useState(null);
  const [newPositionForm, setNewPositionForm] = useState({
    position: '',
    startTime: 0,
    endTime: 0
  });

  // Skip duration for keyboard shortcuts (in seconds)
  const [skipDuration, setSkipDuration] = useState(1);

  // Quick position selector state
  const [showQuickPositionSelector, setShowQuickPositionSelector] = useState(false);

  // Edit position state
  const [editingPositionId, setEditingPositionId] = useState(null);

  const insertScenePosition = async (position) => {
    await axios.post(`${constants.DATA1_API_URL}/scene/${props.sceneId}/positions`, position, {
      headers: { 'Authorization': `${localStorage.getItem('token')}` }
    });
    fetchScenePositions()
  }

  const updateScenePosition = async (position) => {
    await axios.put(`${constants.DATA1_API_URL}/scene/${props.sceneId}/positions`, position, {
      headers: { 'Authorization': `${localStorage.getItem('token')}` }
    });
    fetchScenePositions()
  }

  const removeScenePosition = async (position) => {
    await axios.delete(`${constants.DATA1_API_URL}/scene/${props.sceneId}/positions/${position.id}`, {
      headers: { 'Authorization': `${localStorage.getItem('token')}` }
    });

    fetchScenePositions()
  }

  const fetchScenePositions = () => {
    axios.get(`${constants.DATA1_API_URL}/scene/${props.sceneId}/positions`).then((res) => {
      setScenePositions(res.data);
      positionTimeline(res.data);
      // Transform and set actions directly from response
      setActions(transformPositionsToActions(res.data, videoDuration || 180));
    })
  }

  const fetchPositions = () => {
    axios.get(`${constants.DATA1_API_URL}/scenes/positions`).then((res) => {
      setAllPositions(res.data)
    })
  }

  const fetchPositionCategory = () => {
    axios.get(`${constants.DATA1_API_URL}/scenes/tag-position`).then((res) => {
      setAllPositionTags(res.data)
    })
  }
  const positionTimeline = (positions) => {

    var total = Math.ceil(props.duration)
    const timePer = (time, total) => {
      return Math.ceil(time / total * 100)
    }
    var data = [];
    for (var i = 0; i < positions.length; i++) {
      var start = timePer(positions[i]?.startTime, total)
      var end = timePer(positions[i]?.endTime, total)
      var dur = end - start
      var space;
      if (i == 0) {
        space = timePer(positions[i + 1]?.startTime, total) - 0
        data.push({ type: 'Not Covered', start: timePer(positions[i]?.endTime, total), dur: space })
      }
      else if (i < positions.length - 1) {
        space = timePer(positions[i + 1]?.startTime, total) - timePer(positions[i]?.endTime, total)
        if (space > 0) {
          data.push({ type: 'Covered', start: timePer(positions[i]?.startTime, total), dur: dur })
          data.push({ type: 'Not Covered', start: timePer(positions[i]?.endTime, total), dur: space })
        }
        else {
          data.push({ type: 'Covered', start: timePer(positions[i]?.startTime, total), dur: dur })
        }
      }
      else {
        space = 100 - timePer(positions[i]?.endTime, total)
        if (space > 0) {
          data.push({ type: 'Covered', start: timePer(positions[i]?.startTime, total), dur: dur })
          data.push({ type: 'Not Covered', start: timePer(positions[i]?.endTime, total), dur: space })
        }
        else {
          data.push({ type: 'Covered', start: timePer(positions[i]?.startTime, total), dur: dur })
        }
      }
    }
    setPositionBar(data)

  }

  const selectPosition = useCallback((position) => {
    insertScenePosition({
      scene: props.sceneId,
      position: position.position || position.id,
      startTime: Math.round(timelineCurrentTime),
      endTime: 0
    })
    setShowQuickPositionSelector(false);
  }, [props.sceneId]);

  const handleCloseQuickPositionSelector = useCallback(() => {
    setShowQuickPositionSelector(false);
  }, []);

  // Transform API position data to timeline action format
  const transformPositionsToActions = (positions, duration) => {
    return positions.map(pos => ({
      id: pos.id.toString(),
      type: `P${pos.position}`,
      name: pos.name,
      start: pos.startTime,
      end: pos.endTime === 0 ? (duration || 180) : pos.endTime
    }));
  };

  const handleSelectPositionForAdd = useCallback((position) => {
    if (editingPositionId !== null) {
      // Edit existing position - update position type and auto-fill startTime with current time
      const positionToUpdate = scenePositions.find(p => p.id === editingPositionId);
      if (positionToUpdate) {
        updateScenePosition({
          ...positionToUpdate,
          position: position.id,
          startTime: Math.round(timelineCurrentTime)
        });
      }
      setEditingPositionId(null);
    } else {
      // Add new position
      setSelectedPositionForAdd(position);
      setNewPositionForm({
        position: position.id,
        startTime: Math.round(timelineCurrentTime),
        endTime: Math.round(timelineCurrentTime + 5)
      });
      handleSaveNewPosition();
    }
    setShowPositionGrid(false);
    setSelectedPositionForAdd(null);
  }, [timelineCurrentTime, editingPositionId, scenePositions]);

  const handleClosePositionGrid = useCallback(() => {
    setShowPositionGrid(false);
    setEditingPositionId(null);
  }, []);

  const handleEditPosition = useCallback((positionId) => {
    setEditingPositionId(positionId);
    setShowPositionGrid(true);
  }, []);

  // Update actions whenever props.scenePositions or videoDuration changes
  useEffect(() => {
    if (props.scenePositions && props.scenePositions.length > 0) {
      setActions(transformPositionsToActions(props.scenePositions, videoDuration || 180));
    }
  }, [props.scenePositions, videoDuration]);

  const handleActionChange = (updatedActions) => {
    setActions(updatedActions);
    
    // Sync timeline changes back to positions
    updatedActions.forEach(action => {
      const positionId = parseInt(action.id);
      const position = scenePositions.find(p => p.id === positionId);
      
      if (position && (position.startTime !== action.start || position.endTime !== action.end)) {
        // Update the position with new times
        updateScenePosition({
          id: position.id,
          scene: position.scene,
          position: position.position,
          name: position.name,
          startTime: action.start,
          endTime: action.end
        });
      }
    });
  };

  const handleTimelinePlayingChange = (isPlaying) => {
    setTimelinePlaying(isPlaying);
    if (vidRef.current) {
      if (isPlaying) {
        vidRef.current.play().catch(err => console.log('Play error:', err));
      } else {
        vidRef.current.pause();
      }
    }
  };

  const handleTimelineTimeChange = (newTime) => {
    setTimelineCurrentTime(newTime);
    syncFromTimelineRef.current = true;
    if (vidRef.current) {
      vidRef.current.currentTime = newTime;
    }
    setTimeout(() => {
      syncFromTimelineRef.current = false;
    }, 50);
  };

  const handleVideoTimeUpdate = () => {
    if (vidRef.current && !syncFromTimelineRef.current) {
      setTimelineCurrentTime(vidRef.current.currentTime);
      setVideoTime(vidRef.current.currentTime);
    }
  };

  const handleVideoPlayPause = () => {
    if (vidRef.current && !syncFromTimelineRef.current) {
      setTimelinePlaying(!vidRef.current.paused);
    }
  };

  const handleVideoLoadedMetadata = () => {
    if (vidRef.current) {
      setVideoDuration(vidRef.current.duration);
    }
  };

  const handleAddNewPosition = () => {
    setNewPositionForm({
      position: '',
      startTime: Math.round(timelineCurrentTime),
      endTime: Math.round(timelineCurrentTime + 5)
    });
    setShowAddModal(true);
  };

  const handleSaveNewPosition = async () => {
    if (!newPositionForm.position) {
      alert('Please select a position type');
      return;
    }

    const selectedPosType = allPositions.find(p => p.id === parseInt(newPositionForm.position));
    
    await insertScenePosition({
      scene: props.sceneId,
      position: parseInt(newPositionForm.position),
      name: selectedPosType?.name || 'Position',
      startTime: Math.round(newPositionForm.startTime),
      endTime: Math.round(newPositionForm.endTime)
    });
    
    setShowAddModal(false);
  };

  const handleOpenTimelineWindow = () => {
    // Store scene data in sessionStorage for the new tab to access
    sessionStorage.setItem('timelineEditorData', JSON.stringify({
      sceneId: props.sceneId,
      videoUrl: props.videoUrl,
      videoDuration,
      allPositions,
      scenePositions
    }));
    
    // Open the timeline editor in a new tab
    window.open(
      `/#/timeline-editor?sceneId=${props.sceneId}`,
      '_blank'
    );
  };

  const handleKeyboardSeeking = useCallback((event) => {
    // Debounce to prevent rapid fires from holding down key (50ms minimum between presses)
    const now = Date.now();
    if (now - lastKeyTimeRef.current < 50) {
      return;
    }

    if (!vidRef || !vidRef.current) {
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      lastKeyTimeRef.current = now;
      const newTime = Math.min(vidRef.current.currentTime + skipDuration, vidRef.current.duration);
      vidRef.current.currentTime = newTime;
      setTimelineCurrentTime(newTime);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      lastKeyTimeRef.current = now;
      const newTime = Math.max(vidRef.current.currentTime - skipDuration, 0);
      vidRef.current.currentTime = newTime;
      setTimelineCurrentTime(newTime);
    }
  }, [skipDuration]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyboardSeeking);
    return () => {
      window.removeEventListener('keydown', handleKeyboardSeeking);
    };
  }, [handleKeyboardSeeking]);

  useEffect(() => {
    fetchPositions();
    fetchPositionCategory();
  }, [props.scenePositions]);

  useEffect(() => {
    if (props.scenePositions.length > 0)
      setScenePositions(props.scenePositions)
  }, [props.scenePositions])


  return (
    <>
      <Container fluid>
        {props.editMode ?
        <div style={props.isMobile ? { position: 'sticky', top: 0, zIndex: 10 } : {}}>
        <Row style={{ marginBottom: timelineCollapsed ? '30px' : '20px' }}>
          <Col md="12">
            <FloatingWindow active={!!props.floatingVideo} title={`Scene ${props.sceneId}`} onClose={() => props.setFloatingVideo?.(false)} defaultHeight={300}>
              <VideoContainer isExpanded={timelineCollapsed} $floating={!!props.floatingVideo}>
                <video ref={vidRef} id={`video-${props.sceneId}`} controlsList="nodownload" playsInline={true}
                  onTimeUpdate={handleVideoTimeUpdate}
                  onPlay={handleVideoPlayPause}
                  onPause={handleVideoPlayPause}
                  onLoadedMetadata={handleVideoLoadedMetadata}
                  controls style={{ width: "100%", height: props.floatingVideo ? "100%" : (props.isMobile ? "220px" : (timelineCollapsed ? "500px" : "600px")) }}>
                  <source src={`${props.videoUrl}`} type="video/mp4" />
                </video>
              </VideoContainer>
            </FloatingWindow>
            {props.floatingVideo && <div style={{ color: '#667eea', fontSize: '13px', padding: '6px 0' }}>Video is open in the floating window.</div>}
          </Col>
        </Row>
        </div>:null}

        <Row style={{ marginBottom: '20px' }}>
          {props.editMode ?
          <Col md="12">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h5 style={{ margin: 0 }}>Video Timeline</h5>
                <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '13px', fontWeight: '600', color: '#667eea', backgroundColor: '#f0f3ff', padding: '3px 10px', borderRadius: '4px', letterSpacing: '0.5px' }}>
                  {`${Math.floor(videoTime)}s`}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: 'rgba(255,255,255,0.05)', padding: '5px 10px', borderRadius: '4px' }}>
                  <label htmlFor="skipDuration" style={{ margin: 0, fontSize: '12px', whiteSpace: 'nowrap' }}>Skip (sec):</label>
                  <input 
                    id="skipDuration"
                    type="number" 
                    min="0.1" 
                    max="30"
                    step="0.1" 
                    value={skipDuration}
                    onChange={(e) => setSkipDuration(Math.max(0.1, Math.min(30, parseFloat(e.target.value))) || 1)}
                    style={{ width: '50px', padding: '4px', borderRadius: '3px', border: '1px solid rgba(255,255,255,0.2)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                    title="Set skip duration for arrow keys (⬅️ / ➡️). Max 30 seconds."
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                <Button 
                  variant="outline-info" 
                  size="sm" 
                  onClick={handleOpenTimelineWindow}
                  title="Open timeline in new tab"
                >
                  📋 New Tab
                </Button>
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  onClick={() => setTimelineCollapsed(!timelineCollapsed)}
                >
                  {timelineCollapsed ? '▶ Expand' : '▼ Collapse'}
                </Button>
              </div>
            </div>
            {!timelineCollapsed && (
              <>
                <TimelineComponent
                  durationSec={videoDuration || 180}
                  actions={actions}
                  readOnly={!props.perms.some(e => e.role.id === 2)}
                  onActionChange={handleActionChange}
                  title="Video Timeline"
                  currentTime={timelineCurrentTime}
                  playing={timelinePlaying}
                  onPlayingChange={handleTimelinePlayingChange}
                  onTimeChange={handleTimelineTimeChange}
                />
              </>
            )}
          </Col>
          :null}
        </Row>
        {props.editMode && props.perms && Array.isArray(props.perms) && props.perms.some(e => e && e.role && e.role.id === 2) ? <Row className="m-1">
          <ModernButton variant="light" onClick={() => setShowQuickPositionSelector(true)} className="btn btn-sm">
            Add Positions
          </ModernButton>
          <Button className={`btn btn-sm btn-light`} onClick={fetchScenePositions}><RefreshIcon /></Button>
          <Col>
            <div className="progress">
              {positionBar.map((d,idx) => {
                return <div key={`progress-bar-${d.type}-${idx}`} className={`progress-bar ${d.type == 'Covered' ? 'bg-success' : 'bg-secondary'}`} role="progressbar" style={{ width: `${d.dur}%` }} aria-valuenow={d.dur} aria-valuemin="0" aria-valuemax="100">|</div>
              })}
            </div>
          </Col>

          {/* Quick Position Selector */}
          <PositionSelector
            show={showQuickPositionSelector}
            onClose={handleCloseQuickPositionSelector}
            onSelect={selectPosition}
            positions={allPositions}
            selected={null}
            title="Quick Add Positions"
            buttonText="Add Position"
            searchPlaceholder="🔍 Search positions..."
            groupByCategory={true}
            categoryField="category"
            searchField="name"
            filterCategory="1Main"
          />
        </Row> : null}

        <Row style={{ marginBottom: '30px' }}>
          {scenePositions.map((pos) =>
            <PositionCard key={`card-${pos.id}`}
              style={{ width: "200px" }}
              position={pos}
              positionList={allPositions || []}
              positionTagList={allPositionTags || []}
              roles={props.perms || []}
              refresh={fetchScenePositions}
              update={updateScenePosition}
              remove={removeScenePosition}
              jumpVideo={props.jumpVideo}
              onChangePosition={handleEditPosition} />
          )}
        </Row>

{/* Add New Position with Modern Modal and Search/Filter */}
        {showAddModal && (
          <Modal
            title="Add Position to Scene"
            onClose={() => setShowAddModal(false)}
            position="left"
            width="600px"
            zIndex={1055}
            moveLeft={showPositionGrid}
            footer={
              <div style={{ display: 'flex', gap: '8px', width: '100%', justifyContent: 'flex-end' }}>
                <ModernButton variant="light" onClick={() => setShowAddModal(false)}>
                  <CloseIcon /> Cancel
                </ModernButton>
                <ModernButton 
                  variant="primary" 
                  onClick={handleSaveNewPosition}
                  disabled={!newPositionForm.position}
                >
                  Save Position
                </ModernButton>
              </div>
            }
          >
            <div style={{ marginBottom: '16px' }}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block', fontSize: '12px' }}>
                  Time Range:
                </label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>Start (sec)</label>
                    <Form.Control 
                      type="number" 
                      value={newPositionForm.startTime}
                      onChange={(e) => setNewPositionForm({...newPositionForm, startTime: parseFloat(e.target.value)})}
                      size="sm"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>End (sec)</label>
                    <Form.Control 
                      type="number" 
                      value={newPositionForm.endTime}
                      onChange={(e) => setNewPositionForm({...newPositionForm, endTime: parseFloat(e.target.value)})}
                      size="sm"
                    />
                  </div>
                </div>
              </div>

              <div style={{ fontSize: '12px', fontWeight: '600', color: '#667eea', marginBottom: '8px' }}>
                Select Position Type:
              </div>

              <ModernButton 
                variant="light" 
                onClick={() => setShowPositionGrid(true)}
                style={{ width: '100%', justifyContent: 'center', marginBottom: '8px' }}
              >
                📋 Browse Positions
              </ModernButton>

              {newPositionForm.position && (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#f0f4ff', 
                  borderRadius: '6px', 
                  border: '1px solid #667eea',
                  marginBottom: '8px'
                }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#333' }}>
                    Selected: {allPositions.find(p => p.id === newPositionForm.position)?.name || 'Unknown'}
                  </div>
                </div>
              )}
            </div>
          </Modal>
        )}

        {/* Position Grid Modal with Search and Categories */}
        <PositionSelector
          show={showPositionGrid}
          onClose={handleClosePositionGrid}
          onSelect={handleSelectPositionForAdd}
          positions={allPositions}
          selected={selectedPositionForAdd}
          title="Select Position Type"
          buttonText="Select Position"
          searchPlaceholder="🔍 Search positions by name..."
          groupByCategory={true}
          categoryField="category"
          searchField="name"
          filterCategory="1Main"
        />
      </Container>

    </>
  );
}

const mapStateToProps = (state) => {
  return {
    perms: state.users.permissions.perms || []
  }
}

const VideoContainer = styled.div`
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  margin: 8px 0;
  ${props => props.isExpanded && `
    max-width: 100%;
    width: 100%;
  `}
  ${props => props.$floating && `
    flex: 1 1 auto;
    min-height: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
  `}

  video {
    width: 100%;
    display: block;
  }
`;

export default connect(mapStateToProps)(PositionAdder)